/** Glyph ids for entry icon overrides. UI maps these to Phosphor icons.
 * Annotations themselves persist in the tracked root `.trv` file — see `./trv`. */
export const ENTRY_ICON_IDS = [
  'folder',
  'file',
  'heart',
  'shield',
  'key',
  'wallet',
  'card',
  'id',
  'lock',
  'star',
  'bookmark',
  'pill',
  'stethoscope',
  'home',
  'briefcase',
  'globe',
] as const;

export type EntryIconId = (typeof ENTRY_ICON_IDS)[number];

/** Named icon tint tokens. UI maps these to concrete colors. */
export const ENTRY_ICON_COLOR_IDS = [
  'slate',
  'jade',
  'sky',
  'violet',
  'amber',
  'rose',
  'forest',
  'orange',
] as const;

export type EntryIconColorId = (typeof ENTRY_ICON_COLOR_IDS)[number];

export interface EntryAnnotation {
  /** Override the default file/folder glyph. */
  icon?: EntryIconId;
  /** Tint for the glyph (override or default). */
  color?: EntryIconColorId;
  /** Free-form labels like "health", "finance". */
  tags?: string[];
}

export type EntryAnnotations = Record<string, EntryAnnotation>;

export const normalizeTag = (tag: string) =>
  tag
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 32);

export const normalizeTags = (tags: string[] | undefined): string[] | undefined => {
  if (!tags?.length) return undefined;
  const unique = [...new Set(tags.map(normalizeTag).filter(Boolean))];
  return unique.length ? unique.slice(0, 12) : undefined;
};

export const normalizeAnnotation = (
  annotation: EntryAnnotation | null | undefined,
): EntryAnnotation | undefined => {
  if (!annotation) return undefined;
  const icon =
    annotation.icon && (ENTRY_ICON_IDS as readonly string[]).includes(annotation.icon)
      ? annotation.icon
      : undefined;
  const color =
    annotation.color && (ENTRY_ICON_COLOR_IDS as readonly string[]).includes(annotation.color)
      ? annotation.color
      : undefined;
  const tags = normalizeTags(annotation.tags);
  if (!icon && !color && !tags?.length) return undefined;
  return {
    ...(icon ? { icon } : {}),
    ...(color ? { color } : {}),
    ...(tags ? { tags } : {}),
  };
};

export const sameAnnotation = (
  a: EntryAnnotation | null | undefined,
  b: EntryAnnotation | null | undefined,
): boolean => {
  const left = normalizeAnnotation(a);
  const right = normalizeAnnotation(b);
  if (!left && !right) return true;
  if (!left || !right) return false;
  return (
    left.icon === right.icon &&
    left.color === right.color &&
    (left.tags?.join('\0') ?? '') === (right.tags?.join('\0') ?? '')
  );
};

export const annotationFor = (
  annotations: EntryAnnotations | undefined,
  path: string,
): EntryAnnotation | undefined => annotations?.[path];

export const withAnnotation = (
  annotations: EntryAnnotations | undefined,
  path: string,
  annotation: EntryAnnotation | null,
): EntryAnnotations | undefined => {
  const next = { ...(annotations ?? {}) };
  const normalized = normalizeAnnotation(annotation);
  if (!normalized) delete next[path];
  else next[path] = normalized;
  return Object.keys(next).length ? next : undefined;
};

/** Remap annotation keys after a path move (file or folder prefix). */
export const remapAnnotationsForMove = (
  annotations: EntryAnnotations | undefined,
  source: string,
  destination: string,
): EntryAnnotations | undefined => {
  if (!annotations) return undefined;
  const next: EntryAnnotations = {};
  for (const [path, value] of Object.entries(annotations)) {
    if (path === source || path.startsWith(`${source}/`)) {
      next[`${destination}${path.slice(source.length)}`] = value;
    } else {
      next[path] = value;
    }
  }
  return Object.keys(next).length ? next : undefined;
};

/** Drop annotation keys removed with a file/folder delete. */
export const dropAnnotationsForPath = (
  annotations: EntryAnnotations | undefined,
  source: string,
): EntryAnnotations | undefined => {
  if (!annotations) return undefined;
  const next: EntryAnnotations = {};
  for (const [path, value] of Object.entries(annotations)) {
    if (path === source || path.startsWith(`${source}/`)) continue;
    next[path] = value;
  }
  return Object.keys(next).length ? next : undefined;
};
